/*
INTELLECTUAL PROPERTY NOTICE
© Orbit Tickets Ltd. All rights reserved.
This file contains Custom / Included Intellectual Property created for and owned by the Customer as part of an application developed by Kaleida within the Platform.
Custom / Included Intellectual Property may be used, reproduced, adapted, and incorporated into any application, system, or product developed by or on behalf of the Customer, whether or not such application forms part of the Platform, subject always to the terms of the agreement and provided that such use does not include or rely upon any Protected Intellectual Property of Kaleida Ltd.
For the avoidance of doubt, the inclusion of any application within the Platform does not restrict the Customer's right to use Custom / Included Intellectual Property outside of the Platform.
*/

// IP-NOTICE: CUSTOM
import { Component, Input } from '@angular/core';
import {
	LucideArrowRight,
	LucideBadgePoundSterling,
	LucideBan,
	LucideBanknoteX,
	LucideBell,
	LucideCamera,
	LucideCheck,
	LucideChevronDown,
	LucideChevronUp,
	LucideCircleX,
	LucideCircleHelp,
	LucideCloudDownload,
	LucideCloudUpload,
	LucideCopy,
	LucideCreditCard,
	LucideDownload,
	LucideDynamicIcon,
	LucideExternalLink,
	LucideEye,
	LucideEyeOff,
	LucideFileText,
	LucideGitMerge,
	LucideGripVertical,
	LucideHouse,
	LucideInfo,
	LucideInbox,
	LucideKeyRound,
	LucideListFilter,
	LucideLogOut,
	LucideMenu,
	LucidePencil,
	LucidePlus,
	LucideRefreshCw,
	LucideRotateCcwKey,
	LucideSearch,
	LucideSend,
	LucideShoppingCart,
	LucideStore,
	LucideTriangleAlert,
	LucideTrash2,
	LucideUndo2,
	LucideUpload,
	LucideWallet,
	LucideX,
	provideLucideIcons,
} from '@lucide/angular';

const MATERIAL_TO_LUCIDE_ICON: Readonly<Record<string, string>> = {
	add: 'plus',
	add_a_photo: 'camera',
	add_business: 'store',
	add_card: 'credit-card',
	arrow_forward: 'arrow-right',
	block: 'ban',
	cancel: 'circle-x',
	check: 'check',
	close: 'x',
	cloud_download: 'cloud-download',
	cloud_upload: 'cloud-upload',
	content_copy: 'copy',
	copy: 'copy',
	credit_card_off: 'banknote-x',
	delete: 'trash-2',
	description: 'file-text',
	done: 'check',
	edit: 'pencil',
	error: 'circle-x',
	expand_less: 'chevron-up',
	expand_more: 'chevron-down',
	filter_list: 'list-filter',
	help: 'circle-help',
	home: 'house',
	info: 'info',
	info_outline: 'info',
	lock: 'key-round',
	lock_reset: 'rotate-ccw-key',
	logout: 'log-out',
	menu: 'menu',
	merge: 'git-merge',
	money_off: 'banknote-x',
	move_to_inbox: 'inbox',
	notifications: 'bell',
	open_in_new: 'external-link',
	payment: 'credit-card',
	refresh: 'refresh-cw',
	reorder: 'grip-vertical',
	search: 'search',
	send: 'send',
	shop: 'store',
	shopping_cart: 'shopping-cart',
	undo: 'undo-2',
	upload: 'upload',
	visibility: 'eye',
	visibility_off: 'eye-off',
	wallet: 'wallet',
	warning: 'triangle-alert',
};

@Component({
	selector: 'orbit-icon',
	standalone: true,
	imports: [LucideDynamicIcon],
	template: `
		<svg
			class="orbit-icon"
			[class.orbit-icon--small]="size === 'small'"
			[lucideIcon]="resolvedName"
			[title]="label"
			[attr.aria-hidden]="label ? null : 'true'">
		</svg>
	`,
	host: {
		class: 'orbit-icon-host',
		'[class.orbit-icon-host--small]': "size === 'small'",
		'[attr.role]': "label ? 'img' : null",
		'[attr.aria-label]': 'label || null',
	},
	providers: [
		provideLucideIcons(
			LucideArrowRight,
			LucideBadgePoundSterling,
			LucideBan,
			LucideBanknoteX,
			LucideBell,
			LucideCamera,
			LucideCheck,
			LucideChevronDown,
			LucideChevronUp,
			LucideCircleX,
			LucideCircleHelp,
			LucideCloudDownload,
			LucideCloudUpload,
			LucideCopy,
			LucideCreditCard,
			LucideDownload,
			LucideExternalLink,
			LucideEye,
			LucideEyeOff,
			LucideFileText,
			LucideGitMerge,
			LucideGripVertical,
			LucideHouse,
			LucideInfo,
			LucideInbox,
			LucideKeyRound,
			LucideListFilter,
			LucideLogOut,
			LucideMenu,
			LucidePencil,
			LucidePlus,
			LucideRefreshCw,
			LucideRotateCcwKey,
			LucideSearch,
			LucideSend,
			LucideShoppingCart,
			LucideStore,
			LucideTriangleAlert,
			LucideTrash2,
			LucideUndo2,
			LucideUpload,
			LucideWallet,
			LucideX,
		),
	],
})
export class OrbitIconComponent {
	@Input({ required: true }) name = '';
	@Input() size: 'large' | 'small' = 'large';
	@Input() label = '';

	get resolvedName(): string {
		const normalisedName = this.name.trim();
		return MATERIAL_TO_LUCIDE_ICON[normalisedName] ?? normalisedName.replaceAll('_', '-');
	}
}
